import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { EyeInvisibleOutlined, EyeOutlined, LinkOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Divider, Flex } from "antd";
import { createStyles } from "antd-style";
import { isAxiosError } from "axios";

import {
  login,
  logout,
} from "@av/apps/common-apps/platform/services/authentication/authentication";
import { App } from "@av/components/atoms/app/App";
import { Button } from "@av/components/atoms/Button";
import { Form } from "@av/components/atoms/form/Form";
import { Input } from "@av/components/atoms/input/Input";
import { Typography } from "@av/components/atoms/typography/Typography";
import { AvantiLogo } from "@av/components/molecules/logo/AvantiLogo";
import { usePasswordToggleFocus } from "@av/pages/auth/hooks/usePasswordToggleFocus";
import { getRetryAfterMinutes } from "@av/pages/auth/utils/retry-after";
import { useReturnTo } from "@av/pages/auth/utils/return-to";
import { useRevalidateOnLanguageChange } from "@av/utils/hooks/useRevalidateOnLanguageChange";

const useStyles = createStyles(({ token, css }) => ({
  formWrapper: css`
    width: 100%;
    max-width: 420px;
    padding: ${token.paddingMD}px;

    @media (max-width: ${token.screenMD}px) {
      max-width: 350px;
      padding: 0;
    }
  `,

  divider: css`
    margin: ${token.marginLG}px 0;
    color: ${token.colorTextTertiary};
  `,

  // antd replaces the toggle's className, so the native button is styled from the input wrapper.
  // antd's own toggle styles target `.anticon.ant-input-password-icon` and skip a button without
  // the `anticon` class, so its color, cursor and hover are restored here with the same tokens.
  passwordInput: css`
    button {
      display: inline-flex;
      padding: 0;
      border: 0;
      border-radius: ${token.borderRadiusXS}px;
      background: none;
      font: inherit;
      color: ${token.colorIcon};
      cursor: pointer;
      transition: all ${token.motionDurationSlow};

      &:hover {
        color: ${token.colorIconHover};
      }

      &:focus-visible {
        outline: ${token.lineWidthFocus}px solid ${token.colorPrimaryBorder};
        outline-offset: 1px;
      }
    }
  `,
}));

export function LoginHome() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const { styles } = useStyles();
  const returnTo = useReturnTo();
  const { toggleRef, visibilityToggle } = usePasswordToggleFocus();
  const [form] = Form.useForm();

  useRevalidateOnLanguageChange(form);

  const onFinish = async (values: { username: string; password: string }) => {
    try {
      setLoading(true);

      /**
       * Always logout before login so auth cleanup happens in a deterministic order.
       * This avoids race conditions when the user clicks login while background logout/session cleanup is still running,
       * which can leave stale cross-user session/tracking state behind.
       *
       * Reaching the login page is treated as the source of truth for being logged out.
       * While we could auth check and redirect back into the app, that can create hard-to-detect login loops,
       * especially when the frontend is broken while the backend session is still valid.
       *
       * To avoid blocking the login flow we tolerate failures and let the flow continue to logging in.
       */
      try {
        await Promise.race([logout(), new Promise((res) => setTimeout(res, 2000))]);
      } catch {
        console.warn("logging out failed");
      }

      queryClient.getQueryCache().clear();

      const isLoggedIn = await login(values.username, values.password);

      if (!isLoggedIn) {
        throw new Error("Login failed");
      }

      message.success(t("auth.login.common_success"));
      queryClient.getQueryCache().clear();
      /**
       * Once logged in go back to the page that sent the user here, or the route root,
       * so that the route logic can now be executed.
       */
      navigate(returnTo ?? "/");
    } catch (err) {
      // 429 is returned by the edge WAF rate limit (infra/cdk/lib/waf-stack.ts), not by the login flow.
      if (isAxiosError(err) && err.status === 429) {
        message.error(
          <span data-testid="login-too-many-attempts-message">
            {t("auth.login.common_tooManyAttempts", { count: getRetryAfterMinutes(err) })}
          </span>
        );
      } else if (isAxiosError(err) && (err.status === 401 || err.status === 403)) {
        message.error(
          <span data-testid="login-common-error-message">{t("auth.login.common_error")}</span>
        );
      } else {
        message.error(
          <span data-testid="login-common-server-error-message">
            {t("auth.login.common_server_error")}
          </span>
        );
      }

      console.error("Login error:", err);
    } finally {
      setLoading(false);
    }
  };

  const [loading, setLoading] = useState(false);

  return (
    <Flex vertical align="center" justify="center" style={{ height: "100%", width: "100%" }}>
      <AvantiLogo testIdPrefix="login-home" />
      <div className={styles.formWrapper}>
        <Typography.Title testIdPrefix="right-greeting" level={5} style={{ marginBottom: 4 }}>
          {t("auth.login.common_rightGreeting")}
        </Typography.Title>

        <Form
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          layout="vertical"
          form={form}
          name="login_form"
          onFinish={onFinish}
        >
          <Flex vertical gap={0} style={{ marginBottom: 30 }}>
            <Form.Item
              testIdPrefix="username"
              name="username"
              labelString={t("auth.login.loginForm_login")}
              rules={[
                {
                  required: true,
                  message: (
                    <span data-testid="username-validation-error-message">
                      {t("auth.login.loginForm_missingLogin")}
                    </span>
                  ),
                },
              ]}
              normalize={(value) => value?.trim()}
            >
              <Input
                variant="filled"
                testIdPrefix="login-username"
                placeholder={t("auth.login.loginForm_login")}
                size="large"
              />
            </Form.Item>

            <Form.Item
              testIdPrefix="password"
              name="password"
              labelString={t("auth.login.loginForm_password")}
              rules={[
                {
                  required: true,
                  message: (
                    <span data-testid="password-validation-error-message">
                      {t("auth.login.loginForm_missingPassword")}
                    </span>
                  ),
                },
              ]}
            >
              <Input.Password
                variant="filled"
                placeholder={t("auth.login.loginForm_password")}
                data-testId="password-input"
                testIdPrefix="login"
                size="large"
                className={styles.passwordInput}
                visibilityToggle={visibilityToggle}
                // antd attaches the toggle click handler to this element. A native button makes the
                // toggle keyboard reachable, and Enter/Space trigger that click.
                iconRender={(visible) => (
                  <button
                    ref={toggleRef}
                    type="button"
                    aria-label={
                      visible
                        ? t("auth.login.loginForm_hidePassword")
                        : t("auth.login.loginForm_showPassword")
                    }
                    data-testid={visible ? "login-hide-password-icon" : "login-show-password-icon"}
                  >
                    {visible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  </button>
                )}
              />
            </Form.Item>
          </Flex>

          <Form.Item testIdPrefix="sign-in-button" labelString={t("auth.login.loginForm_signIn")}>
            <Button testIdPrefix="sign-in" type="primary" htmlType="submit" loading={loading} block>
              {t("auth.login.loginForm_signIn")}
            </Button>
          </Form.Item>

          <Divider data-testid="or-divider" plain className={styles.divider}>
            {t("auth.login.loginForm_or")}
          </Divider>

          <Button
            onClick={(e) => {
              e.preventDefault();
              navigate({ pathname: "/login/magic-link", search: location.search });
            }}
            type="default"
            testIdPrefix="magic-link"
            htmlType="submit"
            loading={loading}
            block
            variant="outlined"
            color="primary"
            ghost
            icon={<LinkOutlined />}
          >
            {t("auth.login.loginForm_magicLink")}
          </Button>
        </Form>
      </div>
    </Flex>
  );
}
